import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { authApi } from '../api/auth'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../api/client'
import { jwtDecode } from 'jwt-decode'
import type { LoginRequest, LoginResponse, LoginResult, User } from '../types/auth'
import { isTwoFactorRequired } from '../types/auth'
import { AuthContext, type AuthState } from './authContextValue'
import { isAdminTierRole } from '../lib/roles'
import { clearOfflineSession, isNetworkUnavailable, readOfflineSession, saveOfflineSession } from '../lib/offlineSession'

function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode(token)
    if (!decoded.exp) return true
    return decoded.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    permissions: [],
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
  })
  const offlineExpiryTimer = useRef<number | null>(null)

  const clearOfflineExpiryTimer = useCallback(() => {
    if (offlineExpiryTimer.current !== null) window.clearTimeout(offlineExpiryTimer.current)
    offlineExpiryTimer.current = null
  }, [])

  const restoreOfflineSession = useCallback(async (error: unknown): Promise<boolean> => {
    if (!isNetworkUnavailable(error)) return false
    try {
      const snapshot = await readOfflineSession()
      if (!snapshot || !Number.isFinite(Date.parse(snapshot.refreshTokenExpiresAt)) || Date.parse(snapshot.refreshTokenExpiresAt) <= Date.now()) return false
      clearOfflineExpiryTimer()
      const expireSession = async () => {
        const current = await readOfflineSession().catch(() => null)
        if (!current || current.savedAt !== snapshot.savedAt) return
        const remaining = Date.parse(snapshot.refreshTokenExpiresAt) - Date.now()
        if (remaining > 0) {
          offlineExpiryTimer.current = window.setTimeout(() => { void expireSession() }, Math.min(remaining, 2_147_000_000))
          return
        }
        clearTokens()
        await clearOfflineSession().catch(() => undefined)
        window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
        setState({ user: null, permissions: [], isAuthenticated: false, isAdmin: false, isLoading: false })
      }
      const remaining = Date.parse(snapshot.refreshTokenExpiresAt) - Date.now()
      offlineExpiryTimer.current = window.setTimeout(() => { void expireSession() }, Math.min(remaining, 2_147_000_000))
      setState({
        user: snapshot.user,
        permissions: snapshot.permissions,
        isAuthenticated: true,
        isAdmin: isAdminTierRole(snapshot.user.role),
        isLoading: false,
      })
      return true
    } catch {
      return false
    }
  }, [clearOfflineExpiryTimer])

  const persistOfflineSession = useCallback(async (response: { user: User; permissions: string[]; refreshTokenExpiresAt: string }) => {
    await saveOfflineSession({
      user: response.user,
      permissions: response.permissions,
      refreshTokenExpiresAt: response.refreshTokenExpiresAt,
    }).catch((error: unknown) => console.warn('Salvataggio sessione offline non riuscito', error))
  }, [])

  const initAuth = useCallback(async () => {
    const token = getAccessToken()
    if (!token || isTokenExpired(token)) {
      const refreshToken = getRefreshToken()
      if (refreshToken) {
        try {
          const response = await authApi.refresh(refreshToken)
          setTokens(response.accessToken, response.refreshToken)
          await persistOfflineSession(response)
          clearOfflineExpiryTimer()
          setState({
            user: response.user,
            permissions: response.permissions,
            isAuthenticated: true,
            isAdmin: isAdminTierRole(response.user.role),
            isLoading: false,
          })
          return
        } catch (error: unknown) {
          if (await restoreOfflineSession(error)) return
          clearTokens()
          await clearOfflineSession().catch(() => undefined)
          window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
        }
      } else {
        clearTokens()
        await clearOfflineSession().catch(() => undefined)
        window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
      }
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    try {
      const { user, permissions } = await authApi.getMe()
      const previousSession = await readOfflineSession().catch(() => null)
      if (previousSession && Date.parse(previousSession.refreshTokenExpiresAt) > Date.now()) {
        await persistOfflineSession({ user, permissions, refreshTokenExpiresAt: previousSession.refreshTokenExpiresAt })
      } else if (previousSession) {
        await clearOfflineSession().catch(() => undefined)
      }
      clearOfflineExpiryTimer()
      setState({
        user,
        permissions,
        isAuthenticated: true,
        isAdmin: isAdminTierRole(user.role),
        isLoading: false,
      })
    } catch (error: unknown) {
      if (await restoreOfflineSession(error)) return
      clearTokens()
      await clearOfflineSession().catch(() => undefined)
      window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
      setState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [clearOfflineExpiryTimer, persistOfflineSession, restoreOfflineSession])

  useEffect(() => {
    initAuth()
    const revalidateSession = () => { void initAuth() }
    window.addEventListener('online', revalidateSession)
    return () => window.removeEventListener('online', revalidateSession)
  }, [initAuth])

  const applyLoginResponse = async (response: LoginResponse) => {
    if (state.user && (
      state.user.id !== response.user.id ||
      state.user.agencyId !== response.user.agencyId ||
      JSON.stringify(state.permissions) !== JSON.stringify(response.permissions)
    )) {
      await clearOfflineSession().catch(() => undefined)
      window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
    }
    clearOfflineExpiryTimer()
    setTokens(response.accessToken, response.refreshToken)
    await persistOfflineSession(response)
    setState({
      user: response.user,
      permissions: response.permissions,
      isAuthenticated: true,
      isAdmin: isAdminTierRole(response.user.role),
      isLoading: false,
    })
  }

  const login = async (data: LoginRequest): Promise<LoginResult> => {
    const result = await authApi.login(data)
    if (isTwoFactorRequired(result)) {
      return result
    }
    await applyLoginResponse(result)
    return result
  }

  const completeTwoFactorLogin = async (mfaToken: string, code: string) => {
    const response = await authApi.verifyTwoFactor({ mfaToken, code })
    await applyLoginResponse(response)
  }

  const logout = async () => {
    const accessToken = getAccessToken()
    const serverLogout = authApi.logout(accessToken ?? undefined).catch(() => undefined)
    clearTokens()
    clearOfflineExpiryTimer()
    await clearOfflineSession().catch(() => undefined)
    window.dispatchEvent(new Event('domusaurea:clear-private-cache'))
    setState({
      user: null,
      permissions: [],
      isAuthenticated: false,
      isAdmin: false,
      isLoading: false,
    })
    await serverLogout
  }

  const hasPermission = (permission: string) =>
    state.permissions.includes(permission)

  const hasAnyPermission = (...permissions: string[]) =>
    permissions.some((p) => state.permissions.includes(p))

  const refreshUser = async () => {
    const { user, permissions } = await authApi.getMe()
    const previousSession = await readOfflineSession().catch(() => null)
    if (previousSession && Date.parse(previousSession.refreshTokenExpiresAt) > Date.now()) {
      await persistOfflineSession({ user, permissions, refreshTokenExpiresAt: previousSession.refreshTokenExpiresAt })
    }
    setState((prev) => ({
      ...prev,
      user,
      permissions,
      isAdmin: isAdminTierRole(user.role),
    }))
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        completeTwoFactorLogin,
        logout,
        hasPermission,
        hasAnyPermission,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
